import { type ReactNode, useState } from 'react';
import { pb } from '@/core/api';
import { useAuthStore } from '@/stores/auth';
import {
  PHRASE_MAX as MAX,
  PHRASE_HINT,
  hasForbidden,
} from '@/utils/phrase-validation';
import { ProfileSection } from './profile-section';

const ERR_SAVE = 'Error al guardar.';
const TEXTAREA_CLS =
  'w-full resize-none rounded-lg border border-border-subtle bg-white/[0.05] px-md py-sm text-sm text-text-primary placeholder:text-text-tertiary focus:border-brand-red/50 focus:outline-none';
const SAVE_CLS =
  'rounded-md bg-brand-red px-base py-sm text-sm font-medium text-white transition-colors hover:bg-brand-coral disabled:opacity-50';

function usePhraseForm(initial: string) {
  const refresh = useAuthStore((s) => s.refreshAuth);
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const dirty = value !== initial;
  const submit = async (): Promise<void> => {
    setSaving(true);
    setError('');
    try {
      await pb.send('/api/team/me/phrase', {
        method: 'POST',
        body: { phrase: value },
      });
      await refresh();
    } catch {
      setError(ERR_SAVE);
    } finally {
      setSaving(false);
    }
  };
  return { value, setValue, saving, error, dirty, submit };
}

type FormHandle = ReturnType<typeof usePhraseForm>;

function HintRow({ value, left }: { value: string; left: number }): ReactNode {
  const invalid = hasForbidden(value);
  const hintCls = invalid ? 'text-error' : 'text-text-tertiary';
  const cntCls = left < 0 ? 'text-error' : 'text-text-tertiary';
  return (
    <div className="flex items-start justify-between gap-sm">
      <span className={`text-[11px] leading-snug ${hintCls}`}>
        {PHRASE_HINT}
      </span>
      <span className={`text-xs tabular-nums ${cntCls}`}>
        {left} caracteres
      </span>
    </div>
  );
}

function PhraseFooter({ f, left }: { f: FormHandle; left: number }): ReactNode {
  const invalid = hasForbidden(f.value);
  return (
    <div className="space-y-sm">
      <HintRow value={f.value} left={left} />
      <div className="flex items-center justify-end gap-sm">
        {f.error && <p className="text-xs text-error">{f.error}</p>}
        <button
          type="button"
          onClick={f.submit}
          disabled={!f.dirty || f.saving || left < 0 || invalid}
          className={SAVE_CLS}
        >
          {f.saving ? 'Guardando...' : 'Guardar frase'}
        </button>
      </div>
    </div>
  );
}

export function PhraseSection({ initial }: { initial: string }): ReactNode {
  const f = usePhraseForm(initial);
  const left = MAX - f.value.length;
  return (
    <ProfileSection
      title="Tu frase"
      description="Aparece en tu tarjeta del equipo."
    >
      <textarea
        value={f.value}
        onChange={(e) => setLimited(e.target.value, f.setValue)}
        rows={3}
        className={TEXTAREA_CLS}
        placeholder="Una frase corta que te represente..."
      />
      <PhraseFooter f={f} left={left} />
    </ProfileSection>
  );
}

function setLimited(v: string, set: (s: string) => void): void {
  set(v.slice(0, MAX));
}
